import { useState } from 'react';

import EditIcon from '@mui/icons-material/EditOutlined';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Button,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material';
import { useFormik, getIn } from 'formik';
import { isEmpty, debounce } from 'lodash';
import { Button as ActionButton, useDataProvider, useNotify, useRecordContext, useUpdate } from 'react-admin';
import { DataProviderResource } from 'src/constants';
import { UpdateAnyOfferOrderBodyDto } from 'src/dtos';
import { OfferOrderDetails, User } from 'src/models';
import { isObjectEmpty } from 'src/utils';
import * as yup from 'yup';

interface UpdateOfferOrderFormValues {
  transferDetails?: {
    clearingFirm?: string;
    statementReceivedAt?: string;
    acatSentAt?: string;
    acatReceivedAt?: string;
    acatSubmittedToExternalAt?: string;
    acatResubmittedToExternalAt?: string;
    rejectReason?: string;
  };
  notes?: string;
  representatives?: { id: string; label: string }[];
}

const validationSchema = yup.object().shape({
  transferDetails: yup
    .object()
    .shape({
      clearingFirm: yup.string().optional().nullable(),
      statementReceivedAt: yup.date().optional().nullable(),
      acatSentAt: yup.date().optional().nullable(),
      acatReceivedAt: yup.date().optional().nullable(),
      acatSubmittedToExternalAt: yup.date().optional().nullable(),
      acatResubmittedToExternalAt: yup.date().optional().nullable(),
      rejectReason: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  notes: yup.string().optional().nullable(),
  representatives: yup
    .array()
    .of(yup.object().shape({ id: yup.string().required(), label: yup.string().required() }))
    .optional()
    .nullable(),
});

export const UpdateOfferOrderActionButton = () => {
  const context = useRecordContext<OfferOrderDetails>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [repList, setRepList] = useState<{ id: string; label: string }[]>([]);

  const [update, { isLoading, isError, error, reset }] = useUpdate<UpdateAnyOfferOrderBodyDto & { id: string }>();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const onUpdate = (values: UpdateOfferOrderFormValues) => {
    update(DataProviderResource.OfferOrders, {
      id: context.id,
      data: {
        transferDetails: isObjectEmpty(values.transferDetails)
          ? null
          : {
              clearingFirm: values.transferDetails?.clearingFirm || null,
              statementReceivedAt: values.transferDetails?.statementReceivedAt || null,
              acatSentAt: values.transferDetails?.acatSentAt || null,
              acatReceivedAt: values.transferDetails?.acatReceivedAt || null,
              acatSubmittedToExternalAt: values.transferDetails?.acatSubmittedToExternalAt || null,
              acatResubmittedToExternalAt: values.transferDetails?.acatResubmittedToExternalAt || null,
              rejectReason: values.transferDetails?.rejectReason || null,
            },
        notes: values.notes || null,
        representatives: isEmpty(values.representatives) ? null : values.representatives?.map(rep => rep.id),
      },
    }).then(() => {
      notify('Offer order was successfully updated.', {
        type: 'success',
      });
      _onClose();
    });
  };

  const formik = useFormik<UpdateOfferOrderFormValues>({
    initialValues: {
      transferDetails: {
        clearingFirm: context.transferDetails?.clearingFirm,
        statementReceivedAt: context.transferDetails?.statementReceivedAt,
        acatSentAt: context.transferDetails?.acatSentAt,
        acatReceivedAt: context.transferDetails?.acatReceivedAt,
        acatSubmittedToExternalAt: context.transferDetails?.acatSubmittedToExternalAt,
        acatResubmittedToExternalAt: context.transferDetails?.acatResubmittedToExternalAt,
        rejectReason: context.transferDetails?.rejectReason,
      },
      notes: context.notes,
      representatives: context.representatives?.map(rep => ({ id: rep.id, label: rep.email })),
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: onUpdate,
  });

  const resetModal = () => {
    formik.resetForm();
  };

  const _onClose = () => {
    setIsOpen(false);
    resetModal();
    reset();
  };

  const onSearch = (query: string) => {
    dataProvider
      .getList<User>(DataProviderResource.Users, {
        filter: {
          email: query,
        },
        pagination: { page: 1, perPage: 20 },
        sort: { field: 'email', order: 'ASC' },
      })
      .then(({ data }) => {
        setRepList(data.map(user => ({ id: user.id, label: `${user.email}` })));
      })
      .catch(error => {
        formik.setFieldError('representatives', `Error fetching representatives: ${error.message}`);
      });
  };

  const debounceSearch = debounce(onSearch, 300);

  return (
    <>
      <ActionButton
        label='Edit'
        onClick={() => {
          setIsOpen(true);
        }}>
        <EditIcon />
      </ActionButton>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} fullWidth>
        <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
          <DialogTitle>Offer Order</DialogTitle>
          <DialogContent>
            <Grid container direction='column' spacing={2}>
              <Grid item xs={12} md={12}>
                <Autocomplete
                  options={repList}
                  defaultValue={formik.values.representatives}
                  multiple
                  freeSolo
                  noOptionsText='No representatives found'
                  renderInput={params => (
                    <TextField
                      {...params}
                      key={params.id}
                      variant='standard'
                      margin='dense'
                      label='Representatives'
                      fullWidth
                      sx={{ width: '100%' }}
                    />
                  )}
                  onInputChange={(e, newValue) => {
                    debounceSearch(newValue);
                  }}
                  onChange={(e, newValue) => {
                    formik.setFieldValue('representatives', newValue);
                  }}
                  fullWidth
                  sx={{ width: '100%' }}
                />
              </Grid>
              {context.account.type?.isRetirement && (
                <Grid item xs={6} md={6}>
                  <TextField
                    name='clearingFirm'
                    label='Clearing Firm'
                    variant='standard'
                    margin='dense'
                    disabled={isLoading}
                    error={Boolean(getIn(formik.errors, 'transferDetails.clearingFirm'))}
                    helperText={getIn(formik.errors, 'transferDetails.clearingFirm')}
                    value={formik.values.transferDetails?.clearingFirm}
                    onChange={e => {
                      formik.setFieldValue('transferDetails.clearingFirm', e.target.value);
                    }}
                    fullWidth
                    sx={{ width: '100%' }}
                  />
                </Grid>
              )}
              {context.account.type?.isRetirement && (
                <Grid item xs={6} md={6}>
                  <TextField
                    name='statementReceivedAt'
                    label='Statement Received At'
                    variant='standard'
                    margin='dense'
                    disabled={isLoading}
                    type='date'
                    error={Boolean(getIn(formik.errors, 'transferDetails.statementReceivedAt'))}
                    helperText={getIn(formik.errors, 'transferDetails.statementReceivedAt')}
                    value={formik.values.transferDetails?.statementReceivedAt}
                    onChange={e => {
                      formik.setFieldValue('transferDetails.statementReceivedAt', e.target.value);
                    }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: '50%' }}
                  />
                </Grid>
              )}
              {context.paymentType.isAcat && (
                <>
                  <Grid item xs={6} md={6}>
                    <TextField
                      name='acatSentAt'
                      label='ACAT Sent At'
                      variant='standard'
                      margin='dense'
                      disabled={isLoading}
                      type='date'
                      error={Boolean(getIn(formik.errors, 'transferDetails.acatSentAt'))}
                      helperText={getIn(formik.errors, 'transferDetails.acatSentAt')}
                      value={formik.values.transferDetails?.acatSentAt}
                      onChange={e => {
                        formik.setFieldValue('transferDetails.acatSentAt', e.target.value);
                      }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: '50%' }}
                    />
                  </Grid>
                  <Grid item xs={6} md={6}>
                    <TextField
                      name='acatReceivedAt'
                      label='ACAT Received At'
                      variant='standard'
                      margin='dense'
                      disabled={isLoading}
                      type='date'
                      error={Boolean(getIn(formik.errors, 'transferDetails.acatReceivedAt'))}
                      helperText={getIn(formik.errors, 'transferDetails.acatReceivedAt')}
                      value={formik.values.transferDetails?.acatReceivedAt}
                      onChange={e => {
                        formik.setFieldValue('transferDetails.acatReceivedAt', e.target.value);
                      }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: '50%' }}
                    />
                  </Grid>
                  <Grid item xs={6} md={6}>
                    <TextField
                      name='acatSubmittedToExternalAt'
                      label='ACAT Submitted At'
                      variant='standard'
                      margin='dense'
                      disabled={isLoading}
                      type='date'
                      error={Boolean(getIn(formik.errors, 'transferDetails.acatSubmittedToExternalAt'))}
                      helperText={getIn(formik.errors, 'transferDetails.acatSubmittedToExternalAt')}
                      value={formik.values.transferDetails?.acatSubmittedToExternalAt}
                      onChange={e => {
                        formik.setFieldValue('transferDetails.acatSubmittedToExternalAt', e.target.value);
                      }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: '50%' }}
                    />
                  </Grid>
                  <Grid item xs={6} md={6}>
                    <TextField
                      name='acatResubmittedToExternalAt'
                      label='ACAT Resubmitted At'
                      variant='standard'
                      margin='dense'
                      disabled={isLoading}
                      type='date'
                      error={Boolean(getIn(formik.errors, 'transferDetails.acatResubmittedToExternalAt'))}
                      helperText={getIn(formik.errors, 'transferDetails.acatResubmittedToExternalAt')}
                      value={formik.values.transferDetails?.acatResubmittedToExternalAt}
                      onChange={e => {
                        formik.setFieldValue('transferDetails.acatResubmittedToExternalAt', e.target.value);
                      }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: '50%' }}
                    />
                  </Grid>
                </>
              )}
              {context.account.type?.isRetirement && (
                <Grid item xs={6} md={6}>
                  <TextField
                    name='rejectReason'
                    label='Reject Reason'
                    variant='standard'
                    margin='dense'
                    disabled={isLoading}
                    error={Boolean(getIn(formik.errors, 'transferDetails.rejectReason'))}
                    helperText={getIn(formik.errors, 'transferDetails.rejectReason')}
                    value={formik.values.transferDetails?.rejectReason}
                    onChange={e => {
                      formik.setFieldValue('transferDetails.rejectReason', e.target.value);
                    }}
                    fullWidth
                    sx={{ width: '100%' }}
                  />
                </Grid>
              )}
              <Grid item xs={6} md={6}>
                <TextField
                  name='notes'
                  label='Notes'
                  variant='standard'
                  margin='dense'
                  disabled={isLoading}
                  type='text'
                  error={Boolean(formik.errors.notes)}
                  helperText={formik.errors.notes}
                  value={formik.values.notes}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  multiline
                  rows={5}
                  fullWidth
                  sx={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                {isError && (
                  <Typography variant='body2' color='error'>
                    {`Error updating offer order: ${error}`}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Grid item xs={12} container direction='row' justifyContent='flex-end' spacing={2}>
              <Grid item>
                <Button variant='outlined' color='inherit' size='medium' onClick={_onClose}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button variant='contained' color='primary' size='medium' disabled={isLoading} type='submit'>
                  Update
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};
